import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  type BoardPresentation,
  getBoardPresentations,
  syncBoardPresentations,
} from "@/config/boardPresentation";
import {
  isValidBoardSlug,
  type MixShare,
  type MixSharePercent,
} from "@/config/mixLineup";
import { syncAddedBoardNames } from "@/shared/lib/addedBoardNames";

export const MIX_STORE_VERSION = 4;

export type AddedBoard = Readonly<{
  slug: string;
  boardName: string;
  presentation?: BoardPresentation;
}>;

/** 配信側の登録情報と本人の追加を合成する。訪問しただけの板は候補にしない。 */
export function resolveMixCandidateSlugs(
  registeredSlugs: readonly string[],
  addedBoards: readonly AddedBoard[],
  unavailableSlugs: readonly string[] = [],
  presentations: Readonly<
    Record<string, BoardPresentation>
  > = getBoardPresentations(),
  boardSettings: MixBoardSettings = {},
): readonly string[] {
  return [
    ...new Set([
      ...registeredSlugs.filter(
        (slug) =>
          presentations[slug]?.defaultVisible === true ||
          presentations[slug]?.defaultEnabled === true ||
          (Object.hasOwn(presentations, slug) &&
            Object.hasOwn(boardSettings, slug)),
      ),
      ...addedBoards.map((board) => board.slug),
    ]),
  ].filter((slug) => !unavailableSlugs.includes(slug));
}

/** 総合カタログで選べる取り分(%)の選択肢。「制御なし」はこれとは別枠。 */
export const MIX_SHARE_PERCENTS: readonly MixSharePercent[] = [5, 10, 20, 30];

export type MixBoardSetting = Readonly<{
  enabled: boolean;
  share: MixShare;
}>;

export type MixBoardSettings = Readonly<Record<string, MixBoardSetting>>;

/**
 * 設定を触っていないslugの初期値。全板を平等に扱うため取り分は常に
 * 「制御なし」で始め、ON/OFFは管理画面の公開設定から引く。
 */
export function defaultMixBoardSetting(slug: string): MixBoardSetting {
  return Object.freeze({
    enabled: getBoardPresentations()[slug]?.defaultEnabled ?? false,
    share: "uncapped" as const,
  });
}

type MixExtra = Readonly<{ slug: string; pct: MixSharePercent }>;

/**
 * 総合カタログ1ページの構成。uncappedSlugsはサーバー(catalog-mix)がbump順を
 * 維持したまま混合する板群、extrasは%取り分の上限付きで混ざる板。
 * 全板に%上限を付けた構成ではuncappedSlugsが空になり、ページは
 * 取り分の合計までしか埋まらない。
 */
export type MixSelection = Readonly<{
  uncappedSlugs: readonly string[];
  extras: readonly MixExtra[];
}>;

/**
 * 取り分とは無関係に、総合ページで表示対象になっている板を返す。
 * 全板OFFの場合は、表示対象も空になる。
 */
export function resolveEnabledMixSlugs(
  registeredSlugs: readonly string[],
  boardSettings: MixBoardSettings,
): readonly string[] {
  const enabledSlugs = registeredSlugs.filter(
    (slug) => (boardSettings[slug] ?? defaultMixBoardSetting(slug)).enabled,
  );
  return Object.freeze(enabledSlugs);
}

/**
 * 登録済み総合カタログ対象とユーザー設定から実際に混ぜる構成を決める。
 * どの板も特別扱いせず、ONの板だけを取り分の指定どおりに振り分ける。
 * ONの板が無い場合は、どの板も取得しない空の構成になる。
 */
export function resolveMixSelection(
  registeredSlugs: readonly string[],
  boardSettings: MixBoardSettings,
): MixSelection | null {
  if (registeredSlugs.length === 0) return null;

  const uncappedSlugs: string[] = [];
  const extras: MixExtra[] = [];
  for (const slug of resolveEnabledMixSlugs(registeredSlugs, boardSettings)) {
    const setting = boardSettings[slug] ?? defaultMixBoardSetting(slug);
    if (!setting.enabled) continue;
    if (setting.share === "uncapped") {
      uncappedSlugs.push(slug);
    } else {
      extras.push(Object.freeze({ slug, pct: setting.share }));
    }
  }
  return Object.freeze({
    uncappedSlugs: Object.freeze(uncappedSlugs),
    extras: Object.freeze(extras),
  });
}

interface MixValues {
  boardSettings: MixBoardSettings;
  addedBoards: readonly AddedBoard[];
}

interface MixActions {
  setBoardEnabled: (slug: string, enabled: boolean) => void;
  setBoardShare: (slug: string, share: MixShare) => void;
  resetBoardSettings: () => void;
  addBoard: (board: AddedBoard) => void;
  removeBoard: (slug: string) => void;
  syncRegisteredBoards: (boards: readonly AddedBoard[]) => void;
}

type MixState = MixValues &
  MixActions & {
    unavailableSlugs: readonly string[];
    /** 表示名の合成に使う登録一覧。永続化するのは本人の追加だけ。 */
    registeredBoards: readonly AddedBoard[];
    /** 追加解除後、同じ選択構成でも古いquery cacheを再利用しない。 */
    boardRevision: number;
  };
export type MixStore = UseBoundStore<
  Mutate<StoreApi<MixState>, [["zustand/persist", unknown]]>
>;

const DEFAULT_MIX_VALUES: MixValues = {
  boardSettings: {},
  addedBoards: [],
};

function updateBoardSetting(
  boardSettings: MixBoardSettings,
  slug: string,
  patch: Partial<MixBoardSetting>,
): MixBoardSettings {
  const current = boardSettings[slug] ?? defaultMixBoardSetting(slug);
  return {
    ...boardSettings,
    [slug]: Object.freeze({ ...current, ...patch }),
  };
}

export function createMixStore(storage?: StateStorage): MixStore {
  const persistStorage = resolveMixStateStorage(storage);
  const jsonStorage = persistStorage
    ? createJSONStorage(() => persistStorage)
    : undefined;

  return create<MixState>()(
    persist(
      (set, get) => ({
        ...DEFAULT_MIX_VALUES,
        unavailableSlugs: [],
        registeredBoards: [],
        boardRevision: 0,
        setBoardEnabled: (slug: string, enabled: boolean) =>
          set((state) => ({
            boardSettings: updateBoardSetting(state.boardSettings, slug, {
              enabled,
            }),
          })),
        setBoardShare: (slug: string, share: MixShare) =>
          set((state) => ({
            boardSettings: updateBoardSetting(state.boardSettings, slug, {
              share,
            }),
          })),
        addBoard: (board) =>
          set((state) => {
            if (
              !isValidBoardSlug(board.slug) ||
              board.boardName.trim() === "" ||
              state.addedBoards.some((entry) => entry.slug === board.slug)
            )
              return state;
            if (board.presentation) {
              syncBoardPresentations([
                ...Object.entries(getBoardPresentations()).map(
                  ([slug, presentation]) => ({ slug, presentation }),
                ),
                board,
              ]);
            }
            return {
              addedBoards: [
                ...state.addedBoards,
                Object.freeze({ slug: board.slug, boardName: board.boardName }),
              ],
              boardSettings: updateBoardSetting(
                state.boardSettings,
                board.slug,
                {
                  enabled: true,
                  share: "uncapped",
                },
              ),
              unavailableSlugs: state.unavailableSlugs.filter(
                (slug) => slug !== board.slug,
              ),
              boardRevision: state.boardRevision + 1,
            };
          }),
        removeBoard: (slug) =>
          set((state) => {
            const boardSettings = { ...state.boardSettings };
            delete boardSettings[slug];
            return {
              addedBoards: state.addedBoards.filter(
                (board) => board.slug !== slug,
              ),
              boardSettings,
              unavailableSlugs: state.unavailableSlugs.filter(
                (entry) => entry !== slug,
              ),
              boardRevision: state.boardRevision + 1,
            };
          }),
        syncRegisteredBoards: (boards) => {
          const previous = getBoardPresentations();
          syncBoardPresentations(boards);
          const current = getBoardPresentations();
          const changed =
            Object.keys(previous).length !== Object.keys(current).length ||
            Object.entries(current).some(([slug, config]) => {
              const old = previous[slug];
              return (
                !old ||
                old.defaultVisible !== config.defaultVisible ||
                old.defaultEnabled !== config.defaultEnabled ||
                old.imageUrl !== config.imageUrl
              );
            });
          syncAddedBoardNames([...get().addedBoards, ...boards]);
          set((state) => ({
            registeredBoards: boards,
            boardRevision: state.boardRevision + Number(changed),
            unavailableSlugs: state.addedBoards
              .filter(
                (board) => !boards.some((entry) => entry.slug === board.slug),
              )
              .map((board) => board.slug),
          }));
        },
        resetBoardSettings: () =>
          set((state) => ({
            ...DEFAULT_MIX_VALUES,
            unavailableSlugs: [],
            boardRevision: state.boardRevision + 1,
          })),
      }),
      {
        name: "nijiurachan-mix-settings",
        version: MIX_STORE_VERSION,
        ...(jsonStorage ? { storage: jsonStorage } : {}),
        migrate: migrateMixState,
        merge: mergeMixState,
        partialize: (state: MixState): MixValues => ({
          boardSettings: state.boardSettings,
          addedBoards: state.addedBoards,
        }),
      },
    ),
  );
}

export const useMixStore: MixStore = createMixStore();
syncAddedBoardNames(useMixStore.getState().addedBoards);
useMixStore.subscribe((state, previous) => {
  if (state.addedBoards !== previous.addedBoards)
    syncAddedBoardNames([...state.addedBoards, ...state.registeredBoards]);
});

/** 二次元裏のslug変更(moge→aimg)前の永続キー。 */
const LEGACY_BOARD_SLUG = "moge";
/** 「メイン」表現を廃止する前の取り分値。 */
const LEGACY_UNCAPPED_SHARE = "main";

/**
 * v1の {enabled, pct} を {enabled, share} へ、v2の moge/"main" を
 * aimg/"uncapped" へ引き継ぐ。
 */
export function migrateMixState(persisted: unknown, version: number): unknown {
  if (
    version >= MIX_STORE_VERSION ||
    typeof persisted !== "object" ||
    persisted === null
  ) {
    return persisted;
  }
  const boardSettings = (persisted as Record<string, unknown>).boardSettings;
  if (typeof boardSettings !== "object" || boardSettings === null) {
    return persisted;
  }
  const migrated: Record<string, unknown> = {};
  for (const [slug, setting] of Object.entries(boardSettings)) {
    if (typeof setting !== "object" || setting === null) continue;
    const record = setting as Record<string, unknown>;
    // v1は取り分をpctで持っていた。v2以降はshare。
    const share = version < 2 ? record.pct : record.share;
    migrated[slug === LEGACY_BOARD_SLUG ? "aimg" : slug] = {
      enabled: record.enabled,
      share: share === LEGACY_UNCAPPED_SHARE ? "uncapped" : share,
    };
  }
  const addedBoards = normalizeAddedBoards(
    (persisted as Record<string, unknown>).addedBoards,
  );
  const gore = migrated.gore;
  if (
    typeof gore === "object" &&
    gore !== null &&
    "enabled" in gore &&
    gore.enabled === true &&
    !addedBoards.some((board) => board.slug === "gore")
  ) {
    addedBoards.push({ slug: "gore", boardName: "二次元グロ裏" });
  }
  return { ...persisted, boardSettings: migrated, addedBoards };
}

export function mergeMixState(persisted: unknown, current: MixState): MixState {
  if (typeof persisted !== "object" || persisted === null) return current;
  return {
    ...current,
    boardSettings: normalizePersistedBoardSettings(
      (persisted as Record<string, unknown>).boardSettings,
    ),
    addedBoards: normalizeAddedBoards(
      (persisted as Record<string, unknown>).addedBoards,
    ),
  };
}

/** localStorageの手編集・旧形式が混ざっても有効な設定だけを残す。 */
function normalizePersistedBoardSettings(value: unknown): MixBoardSettings {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }
  const normalized: Record<string, MixBoardSetting> = {};
  for (const [slug, setting] of Object.entries(value)) {
    if (!isValidBoardSlug(slug)) continue;
    if (typeof setting !== "object" || setting === null) continue;
    const record = setting as Record<string, unknown>;
    const share =
      record.share === "uncapped"
        ? ("uncapped" as const)
        : MIX_SHARE_PERCENTS.find((candidate) => candidate === record.share);
    if (typeof record.enabled !== "boolean" || share === undefined) continue;
    normalized[slug] = Object.freeze({ enabled: record.enabled, share });
  }
  return normalized;
}

/** 壊れた保存値や重複を候補へ持ち込まない。API origin等は保存しない。 */
function normalizeAddedBoards(value: unknown): AddedBoard[] {
  if (!Array.isArray(value)) return [];
  const boards: AddedBoard[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const { slug, boardName } = item;
    if (
      typeof slug !== "string" ||
      !isValidBoardSlug(slug) ||
      typeof boardName !== "string" ||
      boardName.trim() === "" ||
      boards.some((board) => board.slug === slug)
    )
      continue;
    boards.push(Object.freeze({ slug, boardName }));
  }
  return boards;
}

function resolveMixStateStorage(
  storage: StateStorage | undefined,
): StateStorage | undefined {
  if (storage) return storage;
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}
